import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CornerDownLeft, FileText, HandCoins, House, Plus, Receipt, ScrollText, Search, Settings, type LucideIcon } from "lucide-react";
import clsx from "clsx";
import { api, type S } from "@/shared/api/client";
import { fold } from "@/shared/lib/fold";
import { useCan, useModule } from "@/shared/lib/session";
import { hrefParts } from "./apps";
import { useAreas } from "./areas";
import { useCreates } from "./creates";
import { createIcon } from "./QuickActions";
import { recordPath } from "./records";
import { useSettingsGroups } from "./SettingsLayout";

interface Command { kind: "page" | "create" | "record" | "act"; label: string; sub?: string; to: string; icon?: LucideIcon }

function matches(c: Command, q: string): boolean {
  const hay = fold(`${c.label} ${c.sub ?? ""}`);
  return fold(q).split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

/** Every page the user may open (each named with its area) and everything they may create, from the apps that are on. */
function useCommands(): { areas: Command[]; pages: Command[]; creates: Command[] } {
  const { t } = useTranslation();
  const areas = useAreas();
  const settings = useSettingsGroups();
  const home: Command = { kind: "page", label: t("nav.home"), to: "/", icon: House };
  const areaCommands = areas.map((a): Command => {
    const first = a.pages.find((p) => !p.more) ?? a.pages[0];
    return { kind: "page", label: t(`areas.${a.key}`), to: first.to, icon: a.icon };
  });
  const pages: Command[] = [
    home,
    ...areas.flatMap((a) => a.pages.map((p): Command => ({ kind: "page", label: p.label, sub: t(`areas.${a.key}`), to: p.to, icon: a.icon }))),
    ...settings.flatMap((g) => g.items.map((i): Command => ({ kind: "page", label: i.label, sub: t("nav.settings"), to: i.to, icon: Settings }))),
  ].filter((c, i, all) => all.findIndex((x) => x.to === c.to) === i);
  const creates = useCreates().map((c): Command => ({ kind: "create", label: t(c.label), to: c.to, icon: createIcon[c.label] ?? Plus }));
  return { areas: [home, ...areaCommands], pages, creates };
}

/**
 * Search everything, from the top of every page (Ctrl+K): go to any page, start anything
 * new, open a record by name or number, and for a customer go straight to what's done
 * with one (an invoice, a payment, their statement).
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const mod = useModule();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const commands = useCommands();
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    if (!open) { setQ(""); setDebounced(""); setActive(0); }
  }, [open]);
  const { data, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api.get<S["SearchHit"][]>("/v1/search", { q: debounced }),
    enabled: open && debounced.length >= 2,
  });
  const typed = q.trim();
  const hits = debounced.length >= 2 ? data ?? [] : [];
  const records = hits.map((h): Command => ({ kind: "record", label: h.title, sub: [t(entityLabelKey(h.entity)), h.subtitle].filter(Boolean).join(" · "), to: recordPath(h.entity, h.id), icon: FileText }));
  // The first customer found brings what's done with a customer.
  const customer = hits.find((h) => h.entity === "core.organization");
  const acts: Command[] = customer && mod("invoicing") ? [
    ...(can("invoicing.invoice.create") ? [{ kind: "act", label: t("shell.actInvoice", { name: customer.title }), to: `/invoicing/invoices/new?org=${customer.id}`, icon: Receipt } as Command] : []),
    ...(can("invoicing.payment.create") ? [{ kind: "act", label: t("shell.actPayment", { name: customer.title }), to: `/invoicing/payments?new=1&org=${customer.id}`, icon: HandCoins } as Command] : []),
    ...(can("invoicing.invoice.read") ? [{ kind: "act", label: t("shell.actStatement", { name: customer.title }), to: `/invoicing/customers/${customer.id}/statement`, icon: ScrollText } as Command] : []),
  ] : [];
  const sections: { title: string; items: Command[] }[] = typed
    ? [
        { title: t("shell.paletteGo"), items: commands.pages.filter((c) => matches(c, typed)).slice(0, 6) },
        { title: t("shell.paletteNew"), items: commands.creates.filter((c) => matches(c, typed)).slice(0, 4) },
        { title: t("shell.paletteActs"), items: acts },
        { title: t("shell.paletteRecords"), items: records },
      ]
    : [
        { title: t("shell.paletteQuick"), items: commands.creates.slice(0, 5) },
        { title: t("shell.paletteAreas"), items: commands.areas },
      ];
  const shown = sections.filter((s) => s.items.length > 0);
  const flat = shown.flatMap((s) => s.items);
  const go = (c: Command) => {
    onClose();
    const { to, search } = hrefParts(c.to);
    void navigate({ to, search: search as never });
  };
  if (!open) return null;
  const end: Record<Command["kind"], ReactNode> = {
    page: <CornerDownLeft className="size-4 ltr:-scale-x-100" />, create: <Plus className="size-4" />, record: <CornerDownLeft className="size-4 ltr:-scale-x-100" />, act: <CornerDownLeft className="size-4 ltr:-scale-x-100" />,
  };
  let index = -1;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/35 px-3 pt-[10vh] backdrop-blur-[2px] sm:px-4" onMouseDown={onClose}>
      <div className="w-full max-w-2xl overflow-hidden rounded-[20px] bg-surface shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-line-2 px-5">
          <Search className="size-5 shrink-0 text-ink-3" />
          <input
            autoFocus
            className="h-14 w-full bg-transparent text-[16px] outline-none placeholder:text-ink-3"
            placeholder={t("shell.paletteHint")}
            aria-label={t("shell.commandHint")}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
              if (e.key === "Enter" && flat[active]) go(flat[active]);
            }}
          />
          <kbd className="num shrink-0 rounded border border-line px-1.5 text-[11px] text-ink-3 max-sm:hidden">Esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2" role="listbox">
          {typed && flat.length === 0 && !(isFetching || q.trim() !== debounced) && <p className="px-4 py-8 text-center text-ink-3">{t("common.noResults")}</p>}
          {shown.map((s) => (
            <div key={s.title} role="group" aria-label={s.title} className="pb-1">
              <div className="px-3 pt-2 pb-1 text-[12px] font-medium text-ink-3">{s.title}</div>
              {s.items.map((c) => {
                const i = ++index;
                const Icon = c.icon;
                return (
                  <button key={c.kind + c.to} type="button" role="option" aria-selected={i === active} onMouseMove={() => setActive(i)} onClick={() => go(c)}
                    className={clsx("flex w-full items-center gap-3 rounded-xl px-3 py-2 text-start", i === active && "bg-paper")}>
                    <span className={clsx("flex size-8 shrink-0 items-center justify-center rounded-lg", c.kind === "create" || c.kind === "act" ? "bg-ledger-soft text-ledger" : "bg-paper text-ink-2")}>
                      {Icon ? <Icon className="size-4" /> : <FileText className="size-4" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{c.label}</span>
                      {c.sub && <span className="block truncate text-[12.5px] text-ink-3">{c.sub}</span>}
                    </span>
                    <span className={clsx("text-ink-3", i !== active && "invisible")}>{end[c.kind]}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function entityLabelKey(e: string): string {
  return ({
    "core.organization": "nav.organizations", "core.contact": "nav.contacts", "core.product": "nav.products",
    "crm.deal": "nav.deals", "crm.lead": "nav.leads", "crm.quote": "nav.quotes", "invoicing.invoice": "nav.invoices",
    "projects.project": "nav.projects", "projects.task": "nav.myTasks",
  } as Record<string, string>)[e] ?? "common.details";
}
